import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';

const AchievementsBadges = ({ achievements, userStats }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Badges', icon: 'Award' },
    { value: 'referrals', label: 'Referrals', icon: 'Users' },
    { value: 'earnings', label: 'Earnings', icon: 'DollarSign' },
    { value: 'quality', label: 'Quality', icon: 'Star' },
    { value: 'milestones', label: 'Milestones', icon: 'Trophy' }
  ];

  const getBadgeIcon = (type) => {
    const iconMap = {
      referrals: 'Users',
      earnings: 'DollarSign',
      quality: 'Star',
      milestones: 'Trophy',
      streak: 'Zap',
      network: 'Network',
      performance: 'TrendingUp'
    };
    return iconMap?.[type] || 'Award';
  };

  const getBadgeGradient = (tier) => {
    const gradients = {
      bronze: 'from-amber-600 to-amber-800',
      silver: 'from-gray-400 to-gray-600',
      gold: 'from-yellow-400 to-yellow-600',
      platinum: 'from-purple-400 to-purple-600',
      diamond: 'from-blue-400 to-blue-600'
    };
    return gradients?.[tier] || 'from-gray-400 to-gray-600';
  };

  const filteredAchievements = achievements?.filter(achievement => 
    selectedCategory === 'all' || achievement?.category === selectedCategory
  );

  const upcomingAchievements = [
    {
      id: 'next-1',
      title: 'Century Club',
      description: 'Complete 100 successful referrals',
      category: 'referrals',
      progress: 87,
      target: 100,
      tier: 'gold',
      reward: '$500 bonus'
    },
    {
      id: 'next-2',
      title: 'Quality Master',
      description: 'Maintain 95% success rate for 6 months',
      category: 'quality',
      progress: 4,
      target: 6,
      tier: 'platinum',
      reward: 'Premium status'
    },
    {
      id: 'next-3',
      title: 'Network Builder',
      description: 'Refer startups from 20 different industries',
      category: 'milestones',
      progress: 16,
      target: 20,
      tier: 'diamond',
      reward: 'Exclusive access'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Current Level & Progress */}
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Intermediary Level</h3>
            <p className="text-sm text-muted-foreground">Your current performance tier</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary">Elite Scout</div>
            <div className="text-sm text-muted-foreground">Level 8</div>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Progress to Next Level</span>
              <span className="text-sm text-muted-foreground">2,340 / 3,000 XP</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-500"
                style={{ width: '78%' }}
              ></div>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">660 XP to go</div>
        </div>
      </div>
      {/* Category Filter */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        {categories?.map((category) => (
          <button
            key={category?.value}
            onClick={() => setSelectedCategory(category?.value)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
              selectedCategory === category?.value
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={category?.icon} size={16} />
            <span>{category?.label}</span>
          </button>
        ))}
      </div>
      {/* Earned Achievements */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Earned Badges</h3>
            <p className="text-sm text-muted-foreground">Your accomplishments and milestones</p>
          </div>
          <div className="text-sm text-muted-foreground">
            {filteredAchievements?.length} badges earned
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredAchievements?.map((achievement) => (
            <div key={achievement?.id} className="group relative">
              <div className="bg-muted/50 rounded-lg p-4 text-center hover:bg-muted transition-colors duration-200 cursor-pointer">
                <div className={`w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br ${getBadgeGradient(achievement?.tier)} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200`}>
                  <Icon name={getBadgeIcon(achievement?.category)} size={24} color="white" />
                </div>
                <h4 className="font-medium text-foreground text-sm mb-1">{achievement?.title}</h4>
                <p className="text-xs text-muted-foreground mb-2">{achievement?.description}</p>
                <div className="text-xs text-accent font-medium">{achievement?.earnedDate}</div>
              </div>
              
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                <div className="bg-popover border border-border rounded-lg p-3 shadow-lg min-w-48">
                  <div className="text-sm font-medium text-popover-foreground mb-1">{achievement?.title}</div>
                  <div className="text-xs text-muted-foreground mb-2">{achievement?.description}</div>
                  <div className="text-xs text-accent">Earned: {achievement?.earnedDate}</div>
                  {achievement?.reward && (
                    <div className="text-xs text-success mt-1">Reward: {achievement?.reward}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Upcoming Achievements */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Next Achievements</h3>
            <p className="text-sm text-muted-foreground">Goals within reach</p>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="Target" size={16} className="mr-2" />
            Set Goals
          </Button>
        </div>

        <div className="space-y-4">
          {upcomingAchievements?.map((achievement) => (
            <div key={achievement?.id} className="bg-muted/30 rounded-lg p-4 border border-border/50">
              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${getBadgeGradient(achievement?.tier)} flex items-center justify-center opacity-60`}>
                  <Icon name={getBadgeIcon(achievement?.category)} size={20} color="white" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-foreground">{achievement?.title}</h4>
                    <span className="text-sm text-muted-foreground">
                      {achievement?.progress}/{achievement?.target}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">{achievement?.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex-1 mr-4">
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className={`bg-gradient-to-r ${getBadgeGradient(achievement?.tier)} h-2 rounded-full transition-all duration-500`}
                          style={{ width: `${(achievement?.progress / achievement?.target) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-xs text-success font-medium">{achievement?.reward}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AchievementsBadges;